import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";

/**
 * Visual booking calendar grid — month view with 3-color day state.
 *
 *   white   = ledig
 *   orange  = delvis bokad
 *   red     = helt bokad (alla 24h täckta)
 *
 * Props:
 *   bookings  — array of { start, slut, user_name?, bokare_namn?, note? }
 *   onPickDay — (Date) => void   called when user clicks a free/partial day
 *   className — extra wrapper classes
 */
const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * For a given calendar day, return:
 *   { state: "free"|"partial"|"full", segments: [{start, end, label}] }
 * "full" means cumulative coverage ≥ 23h (we keep small tolerance for buffer slots).
 */
function dayCoverage(day, bookings) {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + DAY_MS;
  const overlapping = [];
  let coveredMs = 0;
  // Merge overlapping intervals to compute true coverage
  const intervals = [];
  for (const b of bookings) {
    const s = new Date(b.start).getTime();
    const e = new Date(b.slut).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= dayStart || s >= dayEnd) continue;
    const cs = Math.max(s, dayStart);
    const ce = Math.min(e, dayEnd);
    intervals.push([cs, ce]);
    overlapping.push({ start: b.start, end: b.slut, label: b.user_name || b.bokare_namn || "—", note: b.note });
  }
  intervals.sort((a, b) => a[0] - b[0]);
  let mergedEnd = -1;
  for (const [cs, ce] of intervals) {
    const effectiveStart = Math.max(cs, mergedEnd);
    if (ce > effectiveStart) {
      coveredMs += ce - effectiveStart;
      mergedEnd = Math.max(mergedEnd, ce);
    }
  }
  const ratio = coveredMs / DAY_MS;
  let state = "free";
  if (ratio >= 0.96) state = "full";
  else if (ratio > 0) state = "partial";
  return { state, segments: overlapping, ratio };
}

export default function BookingCalendarGrid({ bookings = [], onPickDay, className = "" }) {
  const { t } = useI18n();
  const today = startOfDay(new Date());
  const [cursor, setCursor] = useState(today);

  const monthInfo = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    // Monday-first week: Sun(0) → 6 offset; Mon(1) → 0
    const offset = (first.getDay() + 6) % 7;
    const gridStart = addDays(first, -offset);
    const days = [];
    for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
    return { year, month, days, first };
  }, [cursor]);

  const monthLabel = monthInfo.first.toLocaleString(undefined, { month: "long", year: "numeric" });
  const weekdayLabels = useMemo(() => {
    // Build Mon..Sun based on locale
    const base = new Date(2024, 0, 1); // 1 jan 2024 = Mon
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = addDays(base, i);
      out.push(d.toLocaleString(undefined, { weekday: "short" }));
    }
    return out;
  }, []);

  return (
    <div className={`border border-[var(--ink-200)] bg-[var(--surface)] ${className}`} data-testid="booking-calendar-grid">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--ink-200)]">
        <button
          type="button"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="p-1 hover:bg-[var(--surface-alt)]"
          data-testid="cal-prev"
          aria-label="Previous month"
        >
          <ChevronLeft size={14} />
        </button>
        <div className="font-mono-tech text-xs uppercase tracking-[0.1em]">{monthLabel}</div>
        <button
          type="button"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="p-1 hover:bg-[var(--surface-alt)]"
          data-testid="cal-next"
          aria-label="Next month"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 text-[10px] font-mono-tech uppercase tracking-wider text-[var(--ink-500)] border-b border-[var(--ink-200)]">
        {weekdayLabels.map((d, i) => (
          <div key={i} className="px-1 py-1 text-center">{d}</div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7">
        {monthInfo.days.map((d, idx) => {
          const inMonth = d.getMonth() === monthInfo.month;
          const isPast = d < today && !sameDay(d, today);
          const isToday = sameDay(d, today);
          const { state, segments } = dayCoverage(d, bookings);

          let bg = "transparent";
          let fg = "var(--ink)";
          let title = d.toLocaleDateString();
          if (state === "full") {
            bg = "color-mix(in srgb, var(--brick) 65%, var(--bone))";
            fg = "var(--bone)";
          } else if (state === "partial") {
            bg = "color-mix(in srgb, var(--clay) 35%, var(--bone))";
          }
          if (segments.length) {
            title += "\n" + segments.map((s) => {
              const sd = new Date(s.start);
              const ed = new Date(s.end);
              const hhmm = (x) => `${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
              return `${hhmm(sd)}–${hhmm(ed)} · ${s.label}`;
            }).join("\n");
          }
          const canPick = inMonth && !isPast && state !== "full";

          return (
            <button
              key={idx}
              type="button"
              disabled={!canPick}
              onClick={() => canPick && onPickDay && onPickDay(d)}
              title={title}
              data-testid={`cal-day-${d.toISOString().slice(0, 10)}`}
              data-state={state}
              className={`relative aspect-square border-r border-b border-[var(--ink-200)] text-xs flex items-start justify-end p-1 transition-colors
                ${!inMonth ? "opacity-30" : ""}
                ${isPast ? "cursor-not-allowed opacity-50" : canPick ? "hover:ring-2 hover:ring-[var(--ink)] hover:ring-inset cursor-pointer" : "cursor-not-allowed"}
                ${isToday ? "outline outline-2 outline-[var(--ink)] outline-offset-[-2px] z-10" : ""}
              `}
              style={{ background: bg, color: fg }}
            >
              <span className="font-mono-tech">{d.getDate()}</span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 px-3 py-2 border-t border-[var(--ink-200)] text-[10px] font-mono-tech uppercase tracking-wider text-[var(--ink-500)]">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 border border-[var(--ink-200)] bg-[var(--surface)]" />
          {t("cal.legend.free")}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3" style={{ background: "color-mix(in srgb, var(--clay) 35%, var(--bone))" }} />
          {t("cal.legend.partial")}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3" style={{ background: "color-mix(in srgb, var(--brick) 65%, var(--bone))" }} />
          {t("cal.legend.full")}
        </span>
      </div>
    </div>
  );
}
